<template>
  <div class="events-page">
    <!-- ── Loading ── -->
    <div class="full-page-state" v-if="loading">
      <p>Loading events…</p>
    </div>

    <!-- ── No events ── -->
    <div class="full-page-state" v-else-if="!loading && events.length === 0">
      <div class="no-events">
        <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
        <p class="no-events__title">No events</p>
        <p class="no-events__sub">Check back soon for upcoming events.</p>
      </div>
    </div>

    <!-- ── Main ── -->
    <main class="main-content" v-else>

      <!-- Hero Carousel -->
      <section class="hero-section" v-if="heroEvents.length > 0">
        <div class="hero-track">
          <div
            v-for="(event, i) in heroEvents"
            :key="event.id"
            class="hero-card"
            :class="{
              'hero-card--active': i === heroIndex,
              'hero-card--prev': i !== heroIndex && i === heroPrev,
              'hero-card--next': i !== heroIndex && i === heroNext,
            }"
            @click="i === heroIndex ? goToEvent(event.id) : (heroIndex = i)"
          >
            <img v-if="event.imageUrl" :src="event.imageUrl" class="hero-img" alt="" />
            <div v-else class="hero-img hero-img--placeholder"></div>
            <div v-if="i === heroIndex" class="hero-overlay">
              <span class="hero-title">{{ event.title }}</span>
              <button class="btn-hero" @click.stop="goToEvent(event.id)">Buy tickets</button>
            </div>
          </div>
          <div v-if="heroEvents.length === 0" class="hero-card hero-card--active">
            <div class="hero-img hero-img--placeholder"></div>
          </div>
        </div>
        <!-- Dots -->
        <div class="hero-dots" v-if="heroEvents.length > 1">
          <span
            v-for="(_, i) in heroEvents"
            :key="i"
            class="hero-dot"
            :class="{ 'hero-dot--active': i === heroIndex }"
            @click="heroIndex = i"
          ></span>
        </div>
      </section>

      <!-- Upcoming Section -->
      <section class="upcoming-section" v-if="events.length > 0">
        <h2 class="section-heading">Upcoming</h2>
        <div class="upcoming-grid">
          <div
            v-for="(event, i) in upcomingEvents"
            :key="'up-' + event.id"
            class="upcoming-card"
            :class="{ 'upcoming-card--featured': i === 0 }"
            @click="goToEvent(event.id)"
            data-testid="upcoming-card"
          >
            <div class="upcoming-card__cover">
              <img v-if="event.imageUrl" :src="event.imageUrl" class="upcoming-card__img" alt="" />
              <div v-else class="upcoming-card__img upcoming-card__img--placeholder"></div>
            </div>
            <div class="upcoming-card__body">
              <p class="upcoming-card__title">{{ event.title }}</p>
              <p class="upcoming-card__date">{{ formatDate(event.eventDate) }}</p>
              <div class="upcoming-card__organiser">
                <div class="org-avatar" :style="event.organiserAvatar ? { backgroundImage: `url(${event.organiserAvatar})`, backgroundSize: 'cover' } : {}"></div>
                <span class="org-name">{{ event.organiserName }}</span>
              </div>
              <button class="btn-outline-sm" @click.stop="goToEvent(event.id)">Buy tickets</button>
            </div>
          </div>
        </div>
      </section>

      <!-- All Events Section -->
      <section class="all-events-section">
        <h2 class="section-heading">All Events</h2>

        <!-- Filter Bar -->
        <div class="filter-bar">
          <!-- Category pills -->
          <div class="filter-tabs">
            <button
              v-for="cat in categories"
              :key="cat"
              class="filter-tab"
              :class="{ 'filter-tab--active': activeCategory === cat }"
              @click="activeCategory = cat"
            >{{ cat }}</button>
          </div>

          <!-- Location + Search row -->
          <div class="filter-secondary">
            <div class="location-pill">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg>
              <span>Singapore</span>
            </div>
            <div class="search-pill search-pill--sm">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
              <input v-model="searchQuery" placeholder="Search event" class="search-input" />
            </div>
          </div>
        </div>

        <!-- Event List -->
        <div class="event-list" v-if="filteredEvents.length > 0">
          <div
            v-for="event in filteredEvents"
            :key="'list-' + event.id"
            class="event-row"
            @click="goToEvent(event.id)"
            data-testid="event-row"
          >
            <div class="event-row__date">
              <span class="event-row__dow">{{ getDow(event.eventDate) }}</span>
              <span class="event-row__day">{{ getDay(event.eventDate) }}</span>
              <span class="event-row__mon">{{ getMon(event.eventDate) }}</span>
            </div>
            <div class="event-row__thumb">
              <img v-if="event.imageUrl" :src="event.imageUrl" class="event-row__img" alt="" />
              <div v-else class="event-row__img event-row__img--placeholder"></div>
            </div>
            <div class="event-row__details">
              <span class="event-row__title">{{ event.title }}</span>
              <div class="event-row__meta">
                <div class="org-avatar org-avatar--sm" :style="event.organiserAvatar ? { backgroundImage: `url(${event.organiserAvatar})`, backgroundSize: 'cover' } : {}"></div>
                <span class="event-row__org">{{ event.organiserName }}</span>
              </div>
            </div>
            <div class="event-row__right">
              <span class="event-row__price" v-if="event.minPrice">${{ event.minPrice }}</span>
              <span class="event-row__arrow">›</span>
            </div>
          </div>
        </div>

        <!-- Empty state -->
        <div class="empty-state" v-else-if="!loading">
          <p>No events found.</p>
        </div>

      </section>
    </main>
  </div>
</template>

<script>
import ticketingApi from '@/api/ticketing'

export default {
  name: 'EventsIndex',
  data() {
    return {
      searchQuery: '',
      activeCategory: 'All',
      activeLocation: 'Singapore',
      heroIndex: 0,
      events: [],
      loading: false,
    }
  },
  async created() {
    this.loading = true
    try {
      const res = await ticketingApi.listEvents()
      const raw = res.data || res
      this.events = (Array.isArray(raw) ? raw : []).map((e) => ({
        ...e,
        imageUrl: e.ticketImageUrl || null,
        organiserName: e.organizerName || 'NFTWerks',
        organiserAvatar: e.organizerAvatarUrl || null,
        minPrice: e.tiers && e.tiers.length > 0
          ? Math.min(...e.tiers.filter(t => t.price != null).map((t) => parseFloat(t.price))).toFixed(0)
          : null,
      }))
    } catch {
      // fall through with empty list
    } finally {
      this.loading = false
    }
  },
  computed: {
    activeEvents() {
      const now = Date.now()
      return this.events.filter(e => !e.eventDate || new Date(e.eventDate).getTime() > now)
    },
    heroEvents() {
      return this.activeEvents.slice(0, 3)
    },
    heroPrev() {
      return (this.heroIndex - 1 + this.heroEvents.length) % this.heroEvents.length
    },
    heroNext() {
      return (this.heroIndex + 1) % this.heroEvents.length
    },
    upcomingEvents() {
      return this.activeEvents.slice(0, 6)
    },
    categories() {
      const cats = ['All', ...new Set(this.activeEvents.map(e => e.category?.name).filter(Boolean))]
      return cats
    },
    filteredEvents() {
      const q = this.searchQuery.trim().toLowerCase()
      return this.activeEvents.filter((e) => {
        if (this.activeCategory !== 'All' && e.category?.name !== this.activeCategory) return false
        if (q && !e.title.toLowerCase().includes(q) && !(e.venue || '').toLowerCase().includes(q)) return false
        return true
      })
    },
  },
  methods: {
    goToEvent(id) {
      this.$router.push(`/events/${id}`)
    },
    formatDate(d) {
      if (!d) return ''
      return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).toUpperCase()
    },
    getDow(d) {
      if (!d) return '—'
      return new Date(d).toLocaleDateString('en-GB', { weekday: 'short' }).toUpperCase()
    },
    getDay(d) {
      if (!d) return ''
      return new Date(d).getDate()
    },
    getMon(d) {
      if (!d) return ''
      return new Date(d).toLocaleDateString('en-GB', { month: 'short' }).toUpperCase()
    },
  },
}
</script>

<style scoped lang="scss">
$bg: #f5f5f7;
$surface: #ffffff;
$text: #0a0a0a;
$muted: #6b7280;
$border: #e5e7eb;
$radius-xl: 24px;
$radius-lg: 16px;
$radius-pill: 50px;
$page-px: 80px;
$font: 'Inter', sans-serif;
$gradient: linear-gradient(90deg, #e879b2 0%, #a855f7 100%);

* { box-sizing: border-box; }

.events-page {
  font-family: $font;
  background: $bg;
  min-height: 100vh;
  color: $text;
}

/* ── Main ── */
.main-content {
  display: flex; flex-direction: column; gap: 48px;
  padding: 32px $page-px 80px;
}

/* ── Hero ── */
.hero-section {
  display: flex; flex-direction: column; gap: 16px;
  align-items: center;
}

.hero-track {
  display: flex; align-items: center; justify-content: center;
  gap: 16px; width: 100%; position: relative; height: 460px; overflow: hidden;
}

.hero-card {
  position: absolute; border-radius: 28px; overflow: hidden;
  cursor: pointer; transition: all 0.4s ease;
  left: 50%;
  &--active {
    width: 820px; height: 460px; opacity: 1; z-index: 2;
    transform: translateX(-50%) scale(1);
  }
  &--prev {
    width: 820px; height: 380px; opacity: 0.45; z-index: 1;
    transform: translateX(calc(-50% - 760px)) scale(0.9);
  }
  &--next {
    width: 820px; height: 380px; opacity: 0.45; z-index: 1;
    transform: translateX(calc(-50% + 760px)) scale(0.9);
  }
}

.hero-img {
  width: 100%; height: 100%; object-fit: cover; display: block;
  &--placeholder { background: #c5c5c5; width: 100%; height: 100%; }
}

.hero-overlay {
  position: absolute; bottom: 28px; left: 24px; right: 24px;
  display: flex; align-items: flex-end; justify-content: space-between;
  gap: 12px;
}

.hero-title {
  font-size: 36px; font-weight: 800; color: #fff;
  text-shadow: 0 2px 8px rgba(0,0,0,0.4); flex: 1; line-height: 1.2;
}

.btn-hero {
  background: $gradient; color: #fff; border: none; border-radius: $radius-pill;
  padding: 14px 28px; font-size: 15px; font-weight: 700; cursor: pointer;
  font-family: $font; white-space: nowrap; flex-shrink: 0;
}

@media (max-width: 600px) {
  .hero-overlay {
    flex-direction: column; align-items: flex-start; bottom: 20px; left: 16px; right: 16px;
  }
  .hero-title { font-size: 22px; }
  .btn-hero { padding: 10px 20px; font-size: 14px; }
}

.hero-dots {
  display: flex; gap: 8px;
}
.hero-dot {
  width: 8px; height: 8px; border-radius: 50%; background: $border; cursor: pointer;
  transition: background 0.2s;
  &--active { background: #a855f7; }
}

/* ── Upcoming ── */
.section-heading {
  font-size: 22px; font-weight: 800; margin: 0 0 20px;
}

.upcoming-section { }

.upcoming-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

.upcoming-card {
  background: $surface; border-radius: $radius-xl; overflow: hidden;
  display: flex; flex-direction: column; cursor: pointer;
  transition: box-shadow 0.2s; border: 2px solid transparent;
  &:hover { box-shadow: 0 4px 20px rgba(0,0,0,0.10); }
  &--featured { border-color: #3b82f6; }

  &__cover {
    width: 100%; aspect-ratio: 16/9; overflow: hidden; flex-shrink: 0;
  }
  &__img {
    width: 100%; height: 100%; object-fit: cover; display: block;
    &--placeholder { background: #d1d5db; width: 100%; height: 100%; }
  }
  &__body {
    padding: 16px; display: flex; flex-direction: column; gap: 6px; flex: 1;
  }
  &__title { font-size: 16px; font-weight: 700; margin: 0; line-height: 1.3; }
  &__date { font-size: 13px; color: $muted; margin: 0; }
  &__organiser {
    display: flex; align-items: center; gap: 6px; margin-top: 2px;
  }
}

.org-avatar {
  width: 20px; height: 20px; border-radius: 50%;
  background: $gradient; flex-shrink: 0;
  &--sm { width: 16px; height: 16px; }
}
.org-name { font-size: 12px; font-weight: 600; color: $muted; }

.btn-outline-sm {
  align-self: flex-start; background: $surface; color: #7c3aed;
  border: 1.5px solid #7c3aed; border-radius: $radius-pill;
  padding: 10px 20px; font-size: 14px; font-weight: 600; cursor: pointer;
  font-family: $font; white-space: nowrap; margin-top: auto;
  &:hover { background: #f5f0ff; }
}

/* ── All Events ── */
.all-events-section { display: flex; flex-direction: column; }

.filter-bar {
  display: flex; flex-direction: column;
  margin-bottom: 16px; gap: 10px;
}

.filter-tabs {
  display: flex; align-items: center; gap: 6px;
  overflow-x: auto; -webkit-overflow-scrolling: touch;
  scrollbar-width: none; &::-webkit-scrollbar { display: none; }
  flex-wrap: nowrap; padding-bottom: 2px;
  .filter-tab { flex-shrink: 0; }
}

.filter-tab {
  background: transparent; border: none; border-radius: $radius-pill;
  font-size: 14px; font-weight: 500; color: $text; cursor: pointer;
  padding: 8px 16px; font-family: $font; transition: all 0.15s;
  &--active { background: $text; color: #fff; font-weight: 700; }
  @media (max-width: 640px) {
    font-size: 13px; padding: 6px 12px;
  }
}

.filter-secondary {
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
}

.location-pill {
  display: flex; align-items: center; gap: 5px;
  background: $surface; border: 1.5px solid $border; border-radius: $radius-pill;
  padding: 7px 14px; font-size: 13px; font-weight: 600; color: $text;
  white-space: nowrap; flex-shrink: 0;
  svg { color: #a855f7; flex-shrink: 0; }
}

.search-pill {
  display: flex; align-items: center; gap: 8px;
  background: $surface; border: 1px solid $border; border-radius: $radius-pill;
  padding: 10px 16px;
  svg { color: $muted; flex-shrink: 0; }
  .search-input {
    border: none; outline: none; background: transparent;
    font-size: 14px; font-family: $font; color: $text; width: 180px;
    &::placeholder { color: $muted; }
  }
}

/* ── Event list rows ── */
.event-list { display: flex; flex-direction: column; gap: 8px; }

.event-row {
  background: $surface; border-radius: $radius-lg;
  display: flex; align-items: center; gap: 20px;
  padding: 16px 20px; cursor: pointer;
  transition: box-shadow 0.2s;
  &:hover { box-shadow: 0 2px 12px rgba(0,0,0,0.06); }

  &__date {
    display: flex; flex-direction: column; align-items: center;
    width: 52px; flex-shrink: 0;
  }
  &__dow { font-size: 11px; font-weight: 600; color: $muted; }
  &__day { font-size: 22px; font-weight: 800; line-height: 1; }
  &__mon { font-size: 11px; font-weight: 600; color: $muted; }

  &__thumb { width: 56px; height: 56px; border-radius: 10px; overflow: hidden; flex-shrink: 0; }
  &__img {
    width: 100%; height: 100%; object-fit: cover; display: block;
    &--placeholder { background: #d1d5db; width: 100%; height: 100%; }
  }

  &__details {
    flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 4px;
  }
  &__title { font-size: 16px; font-weight: 700; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  &__meta { display: flex; align-items: center; gap: 6px; }
  &__org { font-size: 13px; color: $muted; }

  &__right { display: flex; align-items: center; gap: 12px; margin-left: auto; flex-shrink: 0; }
  &__price { font-size: 14px; font-weight: 700; }
  &__arrow { font-size: 20px; color: $muted; }
}

/* ── States ── */
.full-page-state {
  display: flex; align-items: center; justify-content: center;
  min-height: 60vh; font-size: 16px; color: $muted;
}

.no-events {
  display: flex; flex-direction: column; align-items: center; gap: 12px; text-align: center;
  svg { color: #d1d5db; }
  &__title { font-size: 22px; font-weight: 700; color: $text; margin: 0; }
  &__sub { font-size: 15px; color: $muted; margin: 0; }
}

.empty-state {
  display: flex; align-items: center; justify-content: center;
  padding: 60px; font-size: 16px; color: $muted;
}

/* ── Responsive ── */
@media (max-width: 1080px) {
  .main-content { padding-left: 20px; padding-right: 20px; }
  .hero-card--active { width: 100%; }
  .hero-card--prev, .hero-card--next { display: none; }
  .upcoming-grid { grid-template-columns: 1fr 1fr; }
}

@media (max-width: 640px) {
  .main-content { padding-left: 12px; padding-right: 12px; gap: 32px; }
  .upcoming-grid { grid-template-columns: 1fr 1fr; gap: 10px; }

  .upcoming-card {
    &__body { padding: 10px; gap: 4px; }
    &__title { font-size: 13px; }
    &__date { font-size: 11px; }
    &__organiser { gap: 4px; margin-top: 0; }
  }
  .org-avatar { width: 14px; height: 14px; }
  .org-name { font-size: 11px; }
  .btn-outline-sm {
    font-size: 11px; padding: 7px 12px; margin-top: 6px;
  }

  .event-row {
    padding: 12px 14px;
    gap: 10px;

    &__date { width: 36px; }
    &__dow { font-size: 9px; }
    &__day { font-size: 18px; }
    &__mon { font-size: 9px; }

    &__thumb { width: 44px; height: 44px; border-radius: 8px; }

    &__title { font-size: 13px; white-space: normal; -webkit-line-clamp: 2; display: -webkit-box; -webkit-box-orient: vertical; overflow: hidden; }
    &__org { font-size: 11px; }

    &__price { font-size: 13px; }
    &__arrow { font-size: 16px; }
  }

  .org-avatar--sm { width: 14px; height: 14px; }

  .filter-secondary { width: 100%; flex-wrap: nowrap; }
  .search-pill { flex: 1; min-width: 0; .search-input { width: 100%; min-width: 0; } }
}
</style>
